import { Checkbox } from "@nextui-org/react";
import { useQuery } from "@tanstack/react-query";
import { TableCell } from "@tremor/react";
import { ChevronDown, DicesIcon } from "lucide-react";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbList,
	BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import Container from "@/components/ui/container";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Table,
	TableBody,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import cacheKeys from "@/config/cache-keys";
import pages from "@/config/pages";
import useFormulas from "@/hooks/useFormulas";
import { cn } from "@/lib/utils";
import AppService from "@/services/AppService";

const CoverageMatrix = () => {
	const formulas = useFormulas();
	const service = new AppService();
	const { t } = useTranslation();

	const { mainAccid, accountId, groupId } = useParams();

	// Marital status options
	const maritalStatusOptions = [
		{
			label: t("calculator.input.option.marital.married"),
			value: "married" as const,
		},
		{
			label: t("calculator.input.option.marital.partner"),
			value: "partner" as const,
		},
		{
			label: t("calculator.input.option.marital.cohabitant"),
			value: "cohabitant" as const,
		},
		{
			label: t("calculator.input.option.marital.single"),
			value: "single" as const,
		},
	];

	const query = useQuery({
		queryKey: cacheKeys.CLIENT_DATA,
		queryFn: async () => service.getClientData(groupId!),
	});

	useEffect(() => {
		if (query.data) {
			formulas.setInsurances(query.data.Result.insurances);
			formulas.setAnnualSalaryInput("600000", query.data.Result.gvalue || 1);
		}
	}, [query.data]);

	const randomizeState = () => {
		const randomAge = Math.floor(Math.random() * 50) + 20;
		const randomInvalidity = Math.floor(Math.random() * 100);
		const randomDisability = Math.floor(Math.random() * 100);
		const randomAnnualSalary = Math.floor(Math.random() * 2_000_000) + 50_000;
		const randomSoleProvider = Math.random() < 0.5;
		const randomMaritalStatus =
			maritalStatusOptions[
				Math.floor(Math.random() * maritalStatusOptions.length)
			];

		formulas.setAgeInput(randomAge.toString());
		formulas.setInvalidityInput(randomInvalidity.toString());
		formulas.setDisabilityInput(randomDisability.toString());
		formulas.setAnnualSalaryInput(
			randomAnnualSalary.toString(),
			query.data?.Result.gvalue || 1,
		);
		formulas.setSoleProviderInput({
			label: "Yes or No",
			value: randomSoleProvider,
		});
		formulas.setMaritalStatusInput(randomMaritalStatus);
	};

	return (
		<Container className="mx-auto">
			<Breadcrumb>
				<BreadcrumbList>
					<BreadcrumbItem>
						<Link
							to={pages.Admin}
							className="text-howden-grey hover:text-gold dark:hover:text-gold-95"
						>
							{t("admin.home")}
						</Link>
					</BreadcrumbItem>
					<BreadcrumbSeparator />
					<BreadcrumbItem>
						<Link
							to={pages.AdminAccount(mainAccid, accountId)}
							className="text-howden-grey hover:text-gold dark:hover:text-gold-95"
						>
							{query.data?.Result.display_name || "Account"}
						</Link>
					</BreadcrumbItem>
				</BreadcrumbList>
			</Breadcrumb>
			<div className="flex gap-4 items-end p-4">
				<Button size="icon" onClick={randomizeState}>
					<DicesIcon />
				</Button>
				<div>
					<Label>Alder</Label>
					<Input
						value={formulas.ageInput}
						onChange={(e) => {
							formulas.setAgeInput(e.target.value);
						}}
					/>
				</div>
				<div>
					<Label>Invaliditet</Label>
					<Input
						value={formulas.invalidityInput}
						onChange={(e) => {
							formulas.setInvalidityInput(e.target.value);
						}}
					/>
				</div>
				<div>
					<Label>Uførhet</Label>
					<Input
						value={formulas.disabilityInput}
						onChange={(e) => {
							formulas.setDisabilityInput(e.target.value);
						}}
					/>
				</div>
				<div>
					<Label>Lønn</Label>
					<Input
						value={formulas.annualSalaryInput}
						onChange={(e) => {
							formulas.setAnnualSalaryInput(
								e.target.value,
								query.data?.Result.gvalue || 1,
							);
						}}
					/>
				</div>
				<div>
					<Label>Sivilstand</Label>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<div className="relative w-full">
								<Input
									value={
										formulas.maritalStatusInput?.label ||
										t("calculator.input.placeholder.select-marital")
									}
									readOnly
									className="w-full text-left rounded-md border p-2 pr-10 cursor-pointer"
								/>
								<span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
									<ChevronDown className="h-4 w-4 text-gray-400" />
								</span>
							</div>
						</DropdownMenuTrigger>
						<DropdownMenuContent
							align="start"
							sideOffset={0}
							className="w-full"
						>
							{maritalStatusOptions.map((option) => (
								<DropdownMenuItem
									key={option.label}
									onClick={() => formulas.setMaritalStatusInput(option)}
								>
									{option.label}
								</DropdownMenuItem>
							))}
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
				<div>
					<Label>Antall barn</Label>
					<Input
						value={formulas.numOfChildrenInput}
						onChange={(e) => {
							formulas.setNumOfChildrenInput(e.target.value);
						}}
					/>
				</div>
				<div className="flex gap-4 items-end">
					{formulas.childAgesInput.map((childAge, index) => {
						return (
							<div key={index}>
								<Label>Child age {index + 1}</Label>
								<Input
									value={childAge}
									onChange={(e) => {
										const ages = formulas.childAgesInput;
										ages[index] = e.target.value;
										formulas.setChildAgesInput(ages);
									}}
								/>
							</div>
						);
					})}
				</div>
				<div>
					<Checkbox
						isSelected={formulas.soleProviderInput?.value}
						onValueChange={(isSelected) => {
							const newValue = { label: "Label", value: isSelected };
							formulas.setSoleProviderInput(newValue);
						}}
					>
						Sole provider
					</Checkbox>
				</div>
			</div>

			<Table>
				<TableHeader className="divide-x bg-moss-green divide-pistachio border-pistachio border-b">
					<TableRow>
						<TableHead
							colSpan={4}
							className="text-center text-pistachio"
						></TableHead>
						<TableHead colSpan={4} className="text-center text-pistachio">
							Medisinsk invaliditet
						</TableHead>
						<TableHead colSpan={4} className="text-center text-pistachio">
							Arbeidsuførhet
						</TableHead>
						<TableHead colSpan={4} className="text-center text-pistachio">
							Dødsfall
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableHeader className="divide-x bg-moss-green divide-pistachio border-pistachio border-b">
					<TableRow>
						<TableHead
							colSpan={4}
							className="text-center text-pistachio"
						></TableHead>
						<TableHead className="text-center text-pistachio" colSpan={2}>
							Ulykke
						</TableHead>
						<TableHead className="text-center text-pistachio" colSpan={2}>
							Sykdom
						</TableHead>
						<TableHead className="text-center text-pistachio" colSpan={2}>
							Ulykke
						</TableHead>
						<TableHead className="text-center text-pistachio" colSpan={2}>
							Sykdom
						</TableHead>
						<TableHead className="text-center text-pistachio" colSpan={2}>
							Ulykke
						</TableHead>
						<TableHead className="text-center text-pistachio" colSpan={2}>
							Sykdom
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableHeader className="divide-x bg-moss-green divide-pistachio border-pistachio border-b">
					<TableRow>
						<TableHead
							colSpan={4}
							className="text-center text-pistachio"
						></TableHead>
						<TableHead className="text-center text-pistachio">Arbeid</TableHead>
						<TableHead className="text-center text-pistachio">Annen</TableHead>
						<TableHead className="text-center text-pistachio">Arbeid</TableHead>
						<TableHead className="text-center text-pistachio">Annen</TableHead>
						<TableHead className="text-center text-pistachio">Arbeid</TableHead>
						<TableHead className="text-center text-pistachio">Annen</TableHead>
						<TableHead className="text-center text-pistachio">Arbeid</TableHead>
						<TableHead className="text-center text-pistachio">Annen</TableHead>
						<TableHead className="text-center text-pistachio">Arbeid</TableHead>
						<TableHead className="text-center text-pistachio">Annen</TableHead>
						<TableHead className="text-center text-pistachio">Arbeid</TableHead>
						<TableHead className="text-center text-pistachio">Annen</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					<TableRow className="divide-x">
						<TableCell colSpan={4}>Lovpålagt yrkesskade</TableCell>
						<CustomTableCell>
							{formulas.getInvalidityPayout(
								{
									show: "compulsory",
									at: "work",
									causedBy: "accident",
									currency: "G",
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell>
							{formulas.getInvalidityPayout(
								{
									show: "compulsory",
									at: "work",
									causedBy: "illness",
									currency: "G",
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell>
							{formulas.getDisabilityPayout(
								{
									show: "compulsory",
									at: "work",
									causedBy: "accident",
									currency: "G",
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell>
							{formulas.getDisabilityPayout(
								{
									show: "compulsory",
									at: "work",
									causedBy: "illness",
									currency: "G",
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell>
							{formulas.getDeathPayout(
								{
									show: "compulsory",
									at: "work",
									causedBy: "accident",
									currency: "G",
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell>
							{formulas.getDeathPayout(
								{
									show: "compulsory",
									at: "work",
									causedBy: "illness",
									currency: "G",
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell></CustomTableCell>
					</TableRow>
					<TableRow className="divide-x">
						<TableCell colSpan={4}>Utvidet yrkesskade</TableCell>
						<CustomTableCell>
							{formulas.getInvalidityPayout(
								{
									show: "employer",
									at: "work",
									causedBy: "accident",
									currency: "G",
									insurancesToInclude: ["extended-wc-accident"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell>
							{formulas.getInvalidityPayout(
								{
									show: "employer",
									at: "work",
									causedBy: "illness",
									currency: "G",
									insurancesToInclude: ["extended-wc-illness"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
					</TableRow>
					<TableRow className="divide-x">
						<TableCell colSpan={4}>Utvidet YS (Død)</TableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell>
							{formulas.getDeathPayout(
								{
									show: "employer",
									at: "work",
									causedBy: "accident",
									currency: "G",
									insurancesToInclude: ["extended-wc-death"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell>
							{formulas.getDeathPayout(
								{
									show: "employer",
									at: "work",
									causedBy: "illness",
									currency: "G",
									insurancesToInclude: ["extended-wc-death"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell></CustomTableCell>
					</TableRow>
					<TableRow className="divide-x">
						<TableCell colSpan={4}>Fritidsulykke</TableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell>
							{formulas.getInvalidityPayout(
								{
									show: "employer",
									at: "other",
									causedBy: "accident",
									currency: "G",
									insurancesToInclude: ["leisure-accident-invalidity"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell>
							{formulas.getDisabilityPayout(
								{
									show: "employer",
									at: "other",
									causedBy: "accident",
									currency: "G",
									insurancesToInclude: ["leisure-accident-disability"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell>
							{formulas.getDeathPayout(
								{
									show: "employer",
									at: "other",
									causedBy: "accident",
									currency: "G",
									insurancesToInclude: ["leisure-accident-death"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
					</TableRow>
					<TableRow className="divide-x">
						<TableCell colSpan={4}>Trygghetsforsikring</TableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell>
							{formulas.getInvalidityPayout(
								{
									show: "employer",
									at: "other",
									causedBy: "accident",
									currency: "G",
									insurancesToInclude: ["trygghetsforsikring"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell>
							{formulas.getDeathPayout(
								{
									show: "employer",
									at: "other",
									causedBy: "accident",
									currency: "G",
									insurancesToInclude: ["trygghetsforsikring"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
					</TableRow>
					<TableRow className="divide-x">
						<TableCell colSpan={4}>Kollektiv ulykke</TableCell>
						<CustomTableCell>
							{formulas.getInvalidityPayout(
								{
									show: "employer",
									at: "work",
									causedBy: "accident",
									currency: "G",
									insurancesToInclude: ["collective-accident-invalidity"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell>
							{formulas.getInvalidityPayout(
								{
									show: "employer",
									at: "other",
									causedBy: "accident",
									currency: "G",
									insurancesToInclude: ["collective-accident-invalidity"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell>
							{formulas.getDeathPayout(
								{
									show: "employer",
									at: "work",
									causedBy: "accident",
									currency: "G",
									insurancesToInclude: ["collective-accident-death"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell>
							{formulas.getDeathPayout(
								{
									show: "employer",
									at: "other",
									causedBy: "accident",
									currency: "G",
									insurancesToInclude: ["collective-accident-death"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
					</TableRow>
					<TableRow className="divide-x">
						<TableCell colSpan={4}>Uførekapital</TableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell>
							{formulas.getDisabilityPayout(
								{
									show: "employer",
									at: "work",
									causedBy: "accident",
									currency: "G",
									insurancesToInclude: ["disability-capital"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell>
							{formulas.getDisabilityPayout(
								{
									show: "employer",
									at: "other",
									causedBy: "accident",
									currency: "G",
									insurancesToInclude: ["disability-capital"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell>
							{formulas.getDisabilityPayout(
								{
									show: "employer",
									at: "work",
									causedBy: "illness",
									currency: "G",
									insurancesToInclude: ["disability-capital"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell>
							{formulas.getDisabilityPayout(
								{
									show: "employer",
									at: "other",
									causedBy: "illness",
									currency: "G",
									insurancesToInclude: ["disability-capital"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
					</TableRow>
					<TableRow className="divide-x">
						<TableCell colSpan={4}>Annen sykdom</TableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell>
							{formulas.getInvalidityPayout(
								{
									show: "employer",
									at: "other",
									causedBy: "illness",
									currency: "G",
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell>
							{formulas.getDisabilityPayout(
								{
									show: "employer",
									at: "other",
									causedBy: "illness",
									currency: "G",
									insurancesToInclude: ["other-illness-disability"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
					</TableRow>
					<TableRow className="divide-x">
						<TableCell colSpan={4}>Gruppeliv - Samordning</TableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell colSpan={4}>
							{formulas.hasCoordinationInsurance() ? <p>Ja</p> : <p>Nei</p>}
						</CustomTableCell>
					</TableRow>
					<TableRow className="divide-x">
						<TableCell colSpan={4}>Gruppeliv - Grunndekning</TableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell colSpan={4}>
							{formulas.getDeathPayout(
								{
									show: "employer",
									at: "other",
									causedBy: "illness",
									currency: "G",

									insurancesToInclude: ["group-life-base-coverage"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
					</TableRow>
					<TableRow className="divide-x">
						<TableCell colSpan={4}>Gruppeliv - Barnetillegg</TableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell colSpan={4}>
							{formulas.getDeathPayout(
								{
									show: "employer",
									at: "other",
									causedBy: "illness",
									currency: "G",

									insurancesToInclude: ["group-life-child-supplement"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
					</TableRow>
					<TableRow className="divide-x">
						<TableCell colSpan={4}>Gruppeliv - Årslønn</TableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell colSpan={4}>
							{formulas.getDeathPayout(
								{
									show: "employer",
									at: "other",
									causedBy: "illness",
									currency: "G",

									insurancesToInclude: ["group-life-annual-salary"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
					</TableRow>
					<TableRow className="divide-x">
						<TableCell colSpan={4}>Gruppeliv - Trapping</TableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell colSpan={4}>
							{formulas.getDeathPayout(
								{
									show: "employer",
									at: "other",
									causedBy: "illness",
									currency: "G",

									insurancesToInclude: ["group-life-gradual-reduction"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
					</TableRow>
					<TableRow className="divide-x">
						<TableCell colSpan={4}>Gruppeliv - Årslønn over x G</TableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell colSpan={4}>
							{formulas.getDeathPayout(
								{
									show: "employer",
									at: "other",
									causedBy: "illness",
									currency: "G",

									insurancesToInclude: ["group-life-annual-salary-above-x-g"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
					</TableRow>
					<TableRow className="divide-x">
						<TableCell colSpan={4}>Gruppeliv - Intervaller</TableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell colSpan={4}>
							{formulas.getDeathPayout(
								{
									show: "employer",
									at: "other",
									causedBy: "illness",
									currency: "G",
									insurancesToInclude: ["group-life-intervals"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
					</TableRow>
					<TableRow className="divide-x">
						<TableCell colSpan={4}>Gruppeliv - HTA</TableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell colSpan={4}>
							{formulas.getDeathPayout(
								{
									show: "employer",
									at: "other",
									causedBy: "illness",
									currency: "G",
									insurancesToInclude: ["group-life-hta"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
					</TableRow>
					<TableRow className="divide-x">
						<TableCell colSpan={4}>Gruppeliv - Forsørgertillegg - G</TableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell colSpan={4}>
							{formulas.getDeathPayout(
								{
									show: "employer",
									at: "other",
									causedBy: "illness",
									currency: "G",
									insurancesToInclude: ["dependents-allowance-base-coverage"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
					</TableRow>
					<TableRow className="divide-x">
						<TableCell colSpan={4}>
							Gruppeliv - Forsørgertillegg - Årslønn
						</TableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell></CustomTableCell>
						<CustomTableCell colSpan={4}>
							{formulas.getDeathPayout(
								{
									show: "employer",
									at: "other",
									causedBy: "illness",
									currency: "G",
									insurancesToInclude: ["dependents-allowance-annual-salary"],
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
					</TableRow>
					<TableRow className="divide-x">
						<TableCell colSpan={4}>Helse</TableCell>
						<CustomTableCell colSpan={12}>
							{formulas.hasHealthInsurance() ? <p>Ja</p> : <p>Nei</p>}
						</CustomTableCell>
					</TableRow>
					<TableRow className="divide-x">
						<TableCell colSpan={4}>Reise</TableCell>
						<CustomTableCell colSpan={12}>
							{formulas.hasTravelInsurance() ? <p>Ja</p> : <p>Nei</p>}
						</CustomTableCell>
					</TableRow>
					<TableRow className="divide-x bg-soft-white">
						<TableCell colSpan={4}>Sum</TableCell>
						<CustomTableCell>
							{formulas.getInvalidityPayout(
								{
									show: "full",
									at: "work",
									causedBy: "accident",
									currency: "G",
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell>
							{formulas.getInvalidityPayout(
								{
									show: "full",
									at: "other",
									causedBy: "accident",
									currency: "G",
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell>
							{formulas.getInvalidityPayout(
								{
									show: "full",
									at: "work",
									causedBy: "illness",
									currency: "G",
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell>
							{formulas.getInvalidityPayout(
								{
									show: "full",
									at: "other",
									causedBy: "illness",
									currency: "G",
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell>
							{formulas.getDisabilityPayout(
								{
									show: "full",
									at: "work",
									causedBy: "accident",
									currency: "G",
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell>
							{formulas.getDisabilityPayout(
								{
									show: "full",
									at: "other",
									causedBy: "accident",
									currency: "G",
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell>
							{formulas.getDisabilityPayout(
								{
									show: "full",
									at: "work",
									causedBy: "illness",
									currency: "G",
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell>
							{formulas.getDisabilityPayout(
								{
									show: "full",
									at: "other",
									causedBy: "illness",
									currency: "G",
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell>
							{formulas.getDeathPayout(
								{
									show: "full",
									at: "work",
									causedBy: "accident",
									currency: "G",
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell>
							{formulas.getDeathPayout(
								{
									show: "full",
									at: "other",
									causedBy: "accident",
									currency: "G",
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell>
							{formulas.getDeathPayout(
								{
									show: "full",
									at: "work",
									causedBy: "illness",
									currency: "G",
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
						<CustomTableCell>
							{formulas.getDeathPayout(
								{
									show: "full",
									at: "other",
									causedBy: "illness",
									currency: "G",
								},
								query.data?.Result.gvalue || 1,
							)}
						</CustomTableCell>
					</TableRow>
				</TableBody>
			</Table>
		</Container>
	);
};

export default CoverageMatrix;

const CustomTableCell = React.forwardRef<
	HTMLTableCellElement,
	React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
	<TableCell
		className={cn("text-center hover:bg-moss-green-20/30", className)}
		ref={ref}
		{...props}
	>
		{props.children}
	</TableCell>
));
